import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { DATE_SEARCH_OPTION_IDS, DEFAULT_FLEXIBLE_DURATION_RANGE } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { addFlexibleMonthsIfNoneSelected } from 'lib/search/searchUtils'
import moment from 'moment'

export type ListingDrawerHotelFlexibleNights =
  Exclude<
    DEFAULT_FLEXIBLE_DURATION_RANGE,
    DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY
  >

export type ListingDrawerHotelDateSelection =
  | {
    type: 'specific'
    checkIn: string
    checkOut: string
  }
  | {
    type: 'flexible'
    flexibleMonths?: string
    flexibleNights: ListingDrawerHotelFlexibleNights
    userSelectedFlexibleMonths: boolean
  }
  | {
    type: 'anytime'
  }

export function isListingDrawerHotelDateSelection(
  value: unknown,
): value is ListingDrawerHotelDateSelection {
  if (!value || typeof value !== 'object') return false

  const type = (value as ListingDrawerHotelDateSelection).type
  return type === 'specific' || type === 'flexible' || type === 'anytime'
}

const HOTEL_FLEXIBLE_NIGHTS: ReadonlySet<string> = new Set(
  Object.values(DEFAULT_FLEXIBLE_DURATION_RANGE).filter(
    (value): value is ListingDrawerHotelFlexibleNights =>
      value !== DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
  ),
)

export function isHotelFlexibleNights(
  value: string,
): value is ListingDrawerHotelFlexibleNights {
  return HOTEL_FLEXIBLE_NIGHTS.has(value)
}

export function getHotelDateSelectionFromSearchState(
  searchState: Pick<GlobalSearchState,
  | 'dateSearchOptionId'
  | 'checkinDate'
  | 'checkoutDate'
  | 'flexibleMonths'
  | 'flexibleNights'
  | 'userSelectedFlexibleMonths'
  >,
): ListingDrawerHotelDateSelection {
  if (searchState.checkinDate && searchState.checkoutDate) {
    return {
      type: 'specific',
      checkIn: searchState.checkinDate.format(ISO_DATE_FORMAT),
      checkOut: searchState.checkoutDate.format(ISO_DATE_FORMAT),
    }
  }

  if (searchState.dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
    const flexibleNights = isHotelFlexibleNights(searchState.flexibleNights)
      ? searchState.flexibleNights
      : DEFAULT_FLEXIBLE_DURATION_RANGE.ANY_DURATION
    return {
      type: 'flexible',
      flexibleMonths: addFlexibleMonthsIfNoneSelected(
        flexibleNights,
        searchState.flexibleMonths,
      ),
      flexibleNights,
      userSelectedFlexibleMonths: searchState.userSelectedFlexibleMonths,
    }
  }

  return { type: 'anytime' }
}

export function getDateSearchOptionId(
  selection: ListingDrawerHotelDateSelection,
): DATE_SEARCH_OPTION_IDS {
  if (selection.type === 'specific') return DATE_SEARCH_OPTION_IDS.SPECIFIC
  if (selection.type === 'flexible') return DATE_SEARCH_OPTION_IDS.FLEXIBLE
  return DATE_SEARCH_OPTION_IDS.ANYTIME
}

export type HotelSearchDateState = Pick<GlobalSearchState,
  | 'checkinDate'
  | 'checkoutDate'
  | 'flexibleMonths'
  | 'flexibleNights'
  | 'userSelectedFlexibleMonths'
  | 'dateSearchOptionId'
  | 'isAnytimeDateSelected'
>

export function getHotelSearchDateState(selection: ListingDrawerHotelDateSelection): HotelSearchDateState {
  return {
    checkinDate: selection.type === 'specific' ? moment(selection.checkIn) : undefined,
    checkoutDate: selection.type === 'specific' ? moment(selection.checkOut) : undefined,
    flexibleMonths: selection.type === 'flexible' ? selection.flexibleMonths : undefined,
    flexibleNights: selection.type === 'flexible' ? selection.flexibleNights : DEFAULT_FLEXIBLE_DURATION_RANGE.EMPTY,
    userSelectedFlexibleMonths: selection.type === 'flexible' && selection.userSelectedFlexibleMonths,
    dateSearchOptionId: getDateSearchOptionId(selection),
    isAnytimeDateSelected: selection.type === 'anytime',
  }
}
